import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { trpc } from "../lib/trpc";

/**
 * Accept invitation page
 * Allows new users to accept their invitation and create an account
 */
export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const token = searchParams.get("token");

  // Define acceptInvitation mutation first (before checkInvitation)
  const acceptInvitation = trpc.auth.acceptInvitation.useMutation({
    onSuccess: ({ redirectTo }) => {
      setIsSubmitted(true);
      toast.success("Invitation accepted!");
      // The server set the session cookies; a full load picks up the new session.
      setTimeout(() => {
        window.location.assign(redirectTo);
      }, 1500);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to accept invitation");
    },
  });

  // Check invitation status
  // Once accepted the token is spent: never re-check it (a refetch would answer
  // NOT_FOUND and bounce to /login, racing the post-accept redirect).
  const accepting = acceptInvitation.isPending || acceptInvitation.isSuccess || isSubmitted;
  const checkInvitation = trpc.auth.checkInvitation.useQuery(
    { token: token! },
    {
      enabled: !!token && !accepting,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    }
  );

  const isCheckingInvitation = !checkInvitation.data;
  const userExists = checkInvitation.data?.userExists ?? false;

  // If the user exists, accept automatically (the accept response signs them in).
  const { mutate: acceptMutate } = acceptInvitation;
  useEffect(() => {
    if (checkInvitation.data?.userExists && token) {
      acceptMutate({ token, name: undefined });
    }
  }, [checkInvitation.data, token, acceptMutate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    acceptInvitation.mutate({ token, name });
  };

  // A missing, bad, expired or used token is a static dead end: no toast, no redirect,
  // no further requests.
  if (!token || (checkInvitation.error && !accepting)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center py-8">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h3 className="font-semibold mb-6">Link expired or already used</h3>
            <Button onClick={() => navigate("/login")}>Back to login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4"
            >
              <UserPlus className="w-6 h-6 text-primary" />
            </motion.div>
            <CardTitle>Accept your invitation</CardTitle>
            <CardDescription>
              {isSubmitted
                ? userExists
                  ? "You've been added to the project!"
                  : "Your account has been created!"
                : isCheckingInvitation
                  ? "Verifying invitation..."
                  : "You've been invited to join TeleCloud Launchpad"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isCheckingInvitation ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-sm text-muted-foreground">
                  Checking invitation status...
                </p>
              </div>
            ) : isSubmitted ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <CheckCircle2 className="w-16 h-16 text-green-700 dark:text-green-300 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">You're all set!</h3>
                <p className="text-sm text-muted-foreground">
                  {userExists
                    ? "You've been added. Taking you there..."
                    : "Your account has been created. Taking you there..."}
                </p>
              </motion.div>
            ) : userExists ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-sm text-muted-foreground">
                  Adding you to the project...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={acceptInvitation.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be displayed to other users
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={acceptInvitation.isPending || !name}
                >
                  {acceptInvitation.isPending ? (
                    <>Accepting invitation...</>
                  ) : (
                    <>Accept invitation</>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
